import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, Trash2, KeyRound, Loader2, Copy, Plus } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CatalogTab } from "@/components/CatalogTab";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [leads, setLeads] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [newTenant, setNewTenant] = useState({ slug: "", name: "" });
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    try {
      const [s, t, l] = await Promise.all([
        api.get("/api/admin/stats"),
        api.get("/api/admin/tenants"),
        api.get("/api/admin/enterprise-leads"),
      ]);
      setStats(s.data); setTenants(t.data); setLeads(l.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admin only");
      } else {
        console.error(e);
      }
    }
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <div className="p-16 text-center text-muted-foreground">Sign in required.</div>;
  if (!user.is_admin) return <div className="p-16 text-center text-muted-foreground" data-testid="admin-denied">Admin access only. Contact support.</div>;
  if (!stats) return <div className="p-16 flex justify-center"><Loader2 className="animate-spin" /></div>;

  const createTenant = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/api/admin/tenants", newTenant);
      setNewKey({ ...data, action: "created" });
      setNewTenant({ slug: "", name: "" });
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const rotate = async (id) => {
    if (!window.confirm("Rotate API key? Old key will stop working.")) return;
    try {
      const { data } = await api.post(`/api/admin/tenants/${id}/rotate-key`);
      setNewKey({ ...data, action: "rotated" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete tenant? Its API key will be invalidated.")) return;
    try { await api.delete(`/api/admin/tenants/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 pt-4 md:pt-6 pb-16">
      <div className="mb-10">
        <div className="eyebrow text-muted-foreground mb-3">Admin console</div>
        <h1 className="hero-title text-4xl md:text-5xl">Network control.</h1>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="rounded-none border border-border bg-background p-1 h-auto" data-testid="admin-tabs">
          <TabsTrigger value="overview" data-testid="admin-tab-overview" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-4">Overview</TabsTrigger>
          <TabsTrigger value="tenants" data-testid="admin-tab-tenants" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-4">Tenants</TabsTrigger>
          <TabsTrigger value="catalog" data-testid="admin-tab-catalog" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-4">Catalog</TabsTrigger>
          <TabsTrigger value="leads" data-testid="admin-tab-leads" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-4">Leads · {leads.length}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-0 border border-border">
            <StatMini label="Users" value={stats.total_users} />
            <StatMini label="Links" value={stats.total_links} />
            <StatMini label="Clicks" value={stats.total_clicks} />
            <StatMini label="Demo" value={stats.demo_links} />
            <StatMini label="Active subs" value={stats.active_subs} />
            <StatMini label="Tenants" value={stats.total_tenants} />
          </div>
        </TabsContent>

        <TabsContent value="tenants">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="eyebrow text-muted-foreground mb-1">Tenants</div>
              <div className="font-heading text-2xl">API keys · {tenants.length}</div>
            </div>
            <Button data-testid="admin-create-tenant" onClick={() => setCreateOpen(true)} className="rounded-none bg-foreground text-background hover:bg-foreground/90"><Plus className="w-4 h-4 mr-2" />New tenant</Button>
          </div>

          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Slug</th>
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Name</th>
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Key prefix</th>
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Links</th>
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Created</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody data-testid="admin-tenants-table">
                {tenants.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0" data-testid={`tenant-row-${t.slug}`}>
                    <td className="p-4 font-mono">{t.slug}</td>
                    <td className="p-4">{t.name}</td>
                    <td className="p-4 font-mono text-xs text-muted-foreground">{t.api_key_prefix}…</td>
                    <td className="p-4 font-mono">{stats.per_tenant_links[t.id] || 0}</td>
                    <td className="p-4 text-xs text-muted-foreground font-mono">{t.created_at?.slice(0, 10)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button data-testid={`rotate-${t.slug}`} onClick={() => rotate(t.id)} className="p-2 hover:bg-secondary" title="Rotate key"><RefreshCw className="w-3.5 h-3.5" /></button>
                        <button data-testid={`delete-tenant-${t.slug}`} onClick={() => del(t.id)} className="p-2 hover:bg-secondary text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="catalog">
          <CatalogTab />
        </TabsContent>

        <TabsContent value="leads">
          {leads.length === 0 ? (
            <div className="p-12 border border-border text-center text-muted-foreground text-sm">No enterprise leads yet.</div>
          ) : (
            <div className="border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="p-4 eyebrow text-muted-foreground font-normal">Name</th>
                    <th className="p-4 eyebrow text-muted-foreground font-normal">Email</th>
                    <th className="p-4 eyebrow text-muted-foreground font-normal">Company</th>
                    <th className="p-4 eyebrow text-muted-foreground font-normal">Message</th>
                    <th className="p-4 eyebrow text-muted-foreground font-normal">When</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="p-4">{l.name}</td>
                      <td className="p-4 font-mono text-xs">{l.email}</td>
                      <td className="p-4 text-muted-foreground">{l.company || "—"}</td>
                      <td className="p-4 max-w-md truncate text-xs text-muted-foreground">{l.message}</td>
                      <td className="p-4 text-xs font-mono text-muted-foreground">{l.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none border border-border sm:max-w-md p-0">
          <DialogHeader className="p-6 border-b border-border">
            <div className="eyebrow text-muted-foreground mb-2">New tenant</div>
            <DialogTitle className="font-heading text-xl font-light">Create tenant + API key</DialogTitle>
          </DialogHeader>
          <form onSubmit={createTenant} className="p-6 space-y-3">
            <Input data-testid="tenant-slug-input" required placeholder="tenant-slug" value={newTenant.slug} onChange={(e) => setNewTenant({ ...newTenant, slug: e.target.value })} className="rounded-none font-mono" />
            <Input data-testid="tenant-name-input" required placeholder="Display name" value={newTenant.name} onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })} className="rounded-none" />
            <Button type="submit" data-testid="tenant-create-submit" disabled={creating} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Create
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newKey} onOpenChange={(o) => !o && setNewKey(null)}>
        <DialogContent className="rounded-none border border-foreground sm:max-w-md p-0" data-testid="new-key-dialog">
          <DialogHeader className="p-6 border-b border-foreground">
            <div className="eyebrow text-accent mb-2">One-time reveal</div>
            <DialogTitle className="font-heading text-xl font-light">API key {newKey?.action}</DialogTitle>
          </DialogHeader>
          <div className="p-6 space-y-4">
            <p className="text-sm text-muted-foreground">Store this key now — it will not be shown again.</p>
            <div className="border border-foreground p-3 font-mono text-xs break-all bg-secondary" data-testid="new-key-value">{newKey?.api_key}</div>
            <Button
              data-testid="copy-new-key"
              onClick={() => { navigator.clipboard.writeText(newKey.api_key); toast.success("Copied"); }}
              className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90"
            ><Copy className="w-4 h-4 mr-2" />Copy key</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatMini({ label, value }) {
  return (
    <div className="p-5 border-r border-b border-border">
      <div className="eyebrow text-muted-foreground mb-1">{label}</div>
      <div className="font-heading text-2xl tracking-tighter tabular-nums">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}
